import React from 'react';
import Icon from '@/components/ui/AppIcon';

interface Specification {
  category: string;
  items: string[];
}

interface TechnicalSpecsProps {
  specifications: Specification[];
}

const TechnicalSpecs = ({ specifications }: TechnicalSpecsProps) => {
  return (
    <section className="py-20">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-display font-bold text-foreground mb-4">
              Technische specificaties
            </h2>
            <p className="text-lg text-muted-foreground">
              Volledige technische details en integratiemogelijkheden
            </p>
          </div>
          
          {/* Table Format for Better Clarity */}
          <div className="glassmorphic rounded-2xl overflow-hidden shadow-silver-glow border border-primary/20">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gradient-to-r from-primary/20 via-primary/15 to-primary/20 border-b-2 border-primary/30">
                    <th className="px-8 py-5 text-left text-sm font-display font-bold text-foreground uppercase tracking-wider">
                      <div className="flex items-center space-x-2">
                        <Icon name="CpuChipIcon" size={20} className="text-primary" />
                        <span>Categorie</span>
                      </div>
                    </th>
                    <th className="px-8 py-5 text-left text-sm font-display font-bold text-foreground uppercase tracking-wider">
                      <div className="flex items-center space-x-2">
                        <Icon name="SparklesIcon" size={20} className="text-accent" />
                        <span>Mogelijkheden & Voordelen</span>
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border/20">
                  {specifications.map((spec, index) => (
                    <tr 
                      key={index}
                      className="hover:bg-gradient-to-r hover:from-primary/10 hover:via-primary/5 hover:to-transparent transition-all duration-300 group"
                    >
                      <td className="px-8 py-7 align-top bg-gradient-to-r from-card/50 to-transparent">
                        <div className="flex items-center space-x-4">
                          <div className="w-12 h-12 bg-gradient-to-br from-primary/30 to-primary/10 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 group-hover:shadow-lg group-hover:shadow-primary/30 transition-all duration-300">
                            <Icon name="CpuChipIcon" size={24} className="text-primary" />
                          </div>
                          <h3 className="text-lg font-display font-bold text-foreground group-hover:text-primary transition-colors duration-300">
                            {spec.category}
                          </h3>
                        </div>
                      </td>
                      <td className="px-8 py-7">
                        <ul className="space-y-4">
                          {spec.items.map((item, itemIndex) => (
                            <li key={itemIndex} className="flex items-start space-x-3 group/item">
                              <div className="w-6 h-6 bg-accent/20 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 group-hover/item:bg-accent/30 transition-colors duration-200">
                                <Icon name="CheckCircleIcon" size={18} className="text-accent" />
                              </div>
                              <span className="text-muted-foreground leading-relaxed group-hover/item:text-foreground transition-colors duration-200">{item}</span>
                            </li>
                          ))}
                        </ul>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Mobile-friendly card view for smaller screens */}
          <div className="lg:hidden mt-8 space-y-6">
            {specifications.map((spec, index) => (
              <div
                key={index}
                className="glassmorphic rounded-xl p-6"
              >
                <div className="flex items-center space-x-3 mb-4">
                  <div className="w-10 h-10 bg-primary/20 rounded-lg flex items-center justify-center">
                    <Icon name="CpuChipIcon" size={24} className="text-primary" />
                  </div>
                  <h3 className="text-xl font-display font-bold text-foreground">
                    {spec.category}
                  </h3>
                </div>
                
                <ul className="space-y-3">
                  {spec.items.map((item, itemIndex) => (
                    <li key={itemIndex} className="flex items-start space-x-3">
                      <Icon name="CheckCircleIcon" size={20} className="text-accent mt-0.5 flex-shrink-0" />
                      <span className="text-muted-foreground">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default TechnicalSpecs;